import { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useToast } from "../context/ToastContext";
import { queryKeys } from "../lib/queryKeys";
import { accessApi } from "../api/access";
import { companiesApi, type CompanyMember } from "../api/companies";
import { PageSkeleton } from "../components/PageSkeleton";
import { EmptyState } from "../components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn, relativeTime } from "../lib/utils";
import {
  Users,
  Search,
  Plus,
  MoreHorizontal,
  Shield,
  ShieldCheck,
  Mail,
  UserCheck,
  Loader2,
  Copy,
  Check,
} from "lucide-react";

type JoinTypeOption = "human" | "agent" | "both";

type Member = CompanyMember;

export function AdminUsers() {
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([{ label: "Administration" }, { label: "Users" }]);
  }, [setBreadcrumbs]);

  const { selectedPracticeId, companies } = usePractice();
  const { pushToast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");

  // ── Invite dialog ─────────────────────────────────────────────
  const [inviteOpen, setInviteOpen] = useState(false);
  const [allowedJoinTypes, setAllowedJoinTypes] = useState<JoinTypeOption>("human");
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const inviteMutation = useMutation({
    mutationFn: () =>
      accessApi.createPracticeInvite(selectedPracticeId!, { allowedJoinTypes }),
    onSuccess: (res) => {
      setInviteUrl(`${window.location.origin}${res.inviteUrl}`);
      setInviteError(null);
    },
    onError: (e: unknown) =>
      setInviteError(e instanceof Error ? e.message : String(e)),
  });

  function openInvite() {
    setAllowedJoinTypes("human");
    setInviteUrl(null);
    setInviteError(null);
    setCopied(false);
    inviteMutation.reset();
    setInviteOpen(true);
  }

  async function copyInviteUrl() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setInviteError("Could not copy to clipboard. Copy the link manually.");
    }
  }

  const { data: members, isLoading } = useQuery({
    queryKey: queryKeys.access.members(selectedPracticeId!),
    queryFn: () => companiesApi.members(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  // ── Assign-role dialog ────────────────────────────────────────
  const [assignMember, setAssignMember] = useState<Member | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  const { data: assignRoles, isLoading: assignRolesLoading } = useQuery({
    queryKey: ["access", "roles", selectedPracticeId],
    queryFn: () => accessApi.listRoles(selectedPracticeId!),
    enabled: !!selectedPracticeId && !!assignMember,
  });

  const assignMutation = useMutation({
    mutationFn: (roleId: string) =>
      accessApi.assignRole(selectedPracticeId!, roleId, {
        principalType: "user",
        principalId: assignMember!.principalId,
      }),
    onSuccess: (_res, roleId) => {
      const role = assignRoles?.find((r) => r.id === roleId);
      pushToast({
        tone: "success",
        title: `Assigned ${role?.name ?? "role"} to ${assignMember?.name ?? assignMember?.email ?? "member"}`,
      });
      setAssignMember(null);
    },
    onError: (e: unknown) =>
      setAssignError(e instanceof Error ? e.message : String(e)),
  });

  function openAssign(member: Member) {
    setAssignError(null);
    assignMutation.reset();
    setAssignMember(member);
  }

  // ── Manage-access dialog (practices + instance admin) ─────────
  const [accessMember, setAccessMember] = useState<Member | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessCompanyIds, setAccessCompanyIds] = useState<Set<string>>(new Set());

  const { data: memberAccess, isLoading: memberAccessLoading } = useQuery({
    queryKey: ["admin", "user-company-access", accessMember?.principalId],
    queryFn: () => accessApi.getUserCompanyAccess(accessMember!.principalId),
    enabled: !!accessMember && accessMember.principalType === "user",
  });

  useEffect(() => {
    if (memberAccess) setAccessCompanyIds(new Set(memberAccess.map((m) => m.companyId)));
  }, [memberAccess]);

  const accessMutation = useMutation({
    mutationFn: () =>
      accessApi.setUserCompanyAccess(accessMember!.principalId, Array.from(accessCompanyIds)),
    onSuccess: () => {
      pushToast({
        tone: "success",
        title: `Updated practice access for ${accessMember?.name ?? accessMember?.email ?? "member"}`,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.access.members(selectedPracticeId!) });
      setAccessMember(null);
    },
    onError: (e: unknown) => setAccessError(e instanceof Error ? e.message : String(e)),
  });

  const promoteMutation = useMutation({
    mutationFn: () => accessApi.promoteInstanceAdmin(accessMember!.principalId),
    onSuccess: () => {
      pushToast({
        tone: "success",
        title: `${accessMember?.name ?? accessMember?.email ?? "Member"} is now an instance admin (access to all practices)`,
      });
      setAccessMember(null);
    },
    onError: (e: unknown) => setAccessError(e instanceof Error ? e.message : String(e)),
  });

  const demoteMutation = useMutation({
    mutationFn: () => accessApi.demoteInstanceAdmin(accessMember!.principalId),
    onSuccess: () => {
      pushToast({
        tone: "success",
        title: `Revoked instance admin from ${accessMember?.name ?? accessMember?.email ?? "member"}`,
      });
      setAccessMember(null);
    },
    onError: (e: unknown) => setAccessError(e instanceof Error ? e.message : String(e)),
  });

  function openManageAccess(member: Member) {
    setAccessError(null);
    setAccessCompanyIds(new Set());
    accessMutation.reset();
    promoteMutation.reset();
    demoteMutation.reset();
    setAccessMember(member);
  }

  function toggleAccessCompany(companyId: string) {
    setAccessCompanyIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
  }

  async function copyEmail(email: string) {
    try {
      await navigator.clipboard.writeText(email);
      pushToast({ tone: "success", title: "Email copied to clipboard" });
    } catch {
      pushToast({ tone: "error", title: "Could not copy email" });
    }
  }

  const filtered = useMemo(() => {
    if (!members) return [];
    return members.filter((m) => {
      const matchesSearch =
        !searchQuery ||
        (m.name ?? "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === "all" || m.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [members, searchQuery, roleFilter]);

  const roleOptions = useMemo(() => {
    if (!members) return [];
    const roles = new Set(members.map((m) => m.role));
    return Array.from(roles).sort();
  }, [members]);

  const stats = useMemo(() => {
    if (!members) return { total: 0, admins: 0, active: 0, invited: 0 };
    return {
      total: members.length,
      admins: members.filter((m) => m.isInstanceAdmin || m.role === "admin" || m.role === "owner").length,
      active: members.filter((m) => m.status !== "invited" && m.status !== "disabled").length,
      invited: members.filter((m) => m.status === "invited").length,
    };
  }, [members]);

  // Practices offered in Manage Access: active ones, plus any (incl. archived)
  // the user already belongs to — so a Save never silently drops an existing
  // archived membership.
  const practiceOptions = useMemo(() => {
    const memberSet = new Set((memberAccess ?? []).map((m) => m.companyId));
    return companies.filter((p) => p.status !== "archived" || memberSet.has(p.id));
  }, [companies, memberAccess]);

  if (isLoading) return <PageSkeleton />;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Users</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage team members, roles, and access permissions
            </p>
          </div>
          <Button size="sm" className="gap-2" onClick={openInvite} disabled={!selectedPracticeId}>
            <Plus className="h-4 w-4" />
            Invite User
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Users", value: stats.total, icon: Users, color: "text-blue-600 bg-blue-50 dark:bg-blue-900/20" },
            { label: "Administrators", value: stats.admins, icon: ShieldCheck, color: "text-purple-600 bg-purple-50 dark:bg-purple-900/20" },
            { label: "Active", value: stats.active, icon: UserCheck, color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20" },
            { label: "Pending Invites", value: stats.invited, icon: Mail, color: "text-amber-600 bg-amber-50 dark:bg-amber-900/20" },
          ].map((stat) => (
            <div key={stat.label} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <div className={cn("rounded-lg p-2", stat.color)}>
                  <stat.icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Search & filter bar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-border bg-background pl-9 pr-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Roles</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Users table */}
        {filtered.length === 0 ? (
          <EmptyState
            icon={Users}
            message={searchQuery ? "No users found. Try adjusting your search." : "No users yet. Invite your first team member to get started."}
          />
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Joined</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Active</th>
                  <th className="w-10 px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((member) => (
                  <tr key={member.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {member.image ? (
                          <img src={member.image} alt="" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold">
                            {(member.name ?? member.email)
                              .split(" ")
                              .map((w) => w[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-foreground">{member.name ?? "—"}</p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {member.isInstanceAdmin ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                          <ShieldCheck className="h-3 w-3" />
                          Instance admin
                        </span>
                      ) : (
                        <span className={cn(
                          "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium",
                          member.role === "owner" || member.role === "admin"
                            ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                            : "bg-muted text-muted-foreground"
                        )}>
                          {(member.role === "owner" || member.role === "admin") && <Shield className="h-3 w-3" />}
                          {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        "inline-flex items-center gap-1.5 text-xs font-medium",
                        member.status === "invited" ? "text-amber-600" : "text-emerald-600"
                      )}>
                        <span className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          member.status === "invited" ? "bg-amber-500" : "bg-emerald-500"
                        )} />
                        {member.status === "invited" ? "Invited" : "Active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {relativeTime(member.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {member.lastActiveAt ? relativeTime(member.lastActiveAt) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" className="text-muted-foreground">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => copyEmail(member.email)}>
                            <Copy className="h-4 w-4" />
                            Copy email
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={member.principalType !== "user"}
                            onSelect={() => openAssign(member)}
                          >
                            <Shield className="h-4 w-4" />
                            Assign role…
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={member.principalType !== "user"}
                            onSelect={() => openManageAccess(member)}
                          >
                            <ShieldCheck className="h-4 w-4" />
                            Manage access…
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite User dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
            <DialogDescription>
              Create a join invite for this practice. Share the resulting link with the person or
              agent you want to grant access.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Allowed join types</label>
              <Select
                value={allowedJoinTypes}
                onValueChange={(v) => setAllowedJoinTypes(v as JoinTypeOption)}
                disabled={inviteMutation.isPending || !!inviteUrl}
              >
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue placeholder="Select join type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="human">Human</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {inviteUrl && (
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Invite link</label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={inviteUrl} readOnly className="font-mono text-xs" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 shrink-0"
                    onClick={copyInviteUrl}
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Anyone with this link can request to join this practice.
                </p>
              </div>
            )}

            {inviteError && <p className="text-sm text-destructive">{inviteError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setInviteOpen(false)}>
              {inviteUrl ? "Done" : "Cancel"}
            </Button>
            {!inviteUrl && (
              <Button
                size="sm"
                onClick={() => inviteMutation.mutate()}
                disabled={inviteMutation.isPending}
              >
                {inviteMutation.isPending && (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                )}
                Create invite
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign role dialog */}
      <Dialog open={!!assignMember} onOpenChange={(open) => !open && setAssignMember(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Assign role to {assignMember?.name ?? assignMember?.email ?? "member"}
            </DialogTitle>
            <DialogDescription>
              Choose a role to grant this member. Assigning replaces this member's permission grants
              with the role's.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {assignRolesLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading roles…
              </div>
            ) : (assignRoles ?? []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No roles yet — create one in Admin → Roles.
              </div>
            ) : (
              <div className="space-y-1.5">
                {(assignRoles ?? []).map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    disabled={assignMutation.isPending}
                    onClick={() => assignMutation.mutate(role.id)}
                    className={cn(
                      "w-full text-left flex items-center justify-between gap-3 rounded-md border px-3 py-2 transition-colors",
                      "border-border bg-card hover:border-primary/30 disabled:opacity-50",
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{role.name}</p>
                      {role.description && (
                        <p className="text-xs text-muted-foreground truncate">{role.description}</p>
                      )}
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Shield className="h-3.5 w-3.5" />
                      {role.permissionKeys.length} perm
                      {role.permissionKeys.length !== 1 ? "s" : ""}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {assignError && <p className="text-sm text-destructive">{assignError}</p>}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAssignMember(null)}
              disabled={assignMutation.isPending}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage access dialog */}
      <Dialog open={!!accessMember} onOpenChange={(open) => !open && setAccessMember(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Manage access for {accessMember?.name ?? accessMember?.email ?? "member"}
            </DialogTitle>
            <DialogDescription>
              Choose which practices this user belongs to, or grant instance admin for full access to
              every practice. Changes apply immediately across the platform.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Practices */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Practices
              </p>
              {memberAccessLoading ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading access…
                </div>
              ) : practiceOptions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  No practices yet.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {practiceOptions.map((p) => {
                    const checked = accessCompanyIds.has(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        disabled={accessMutation.isPending}
                        onClick={() => toggleAccessCompany(p.id)}
                        className={cn(
                          "w-full text-left flex items-center justify-between gap-3 rounded-md border px-3 py-2 transition-colors disabled:opacity-50",
                          checked
                            ? "border-primary/40 bg-primary/5"
                            : "border-border bg-card hover:border-primary/30",
                        )}
                      >
                        <span className="text-sm font-medium text-foreground truncate">{p.name}</span>
                        <span
                          className={cn(
                            "shrink-0 flex h-4 w-4 items-center justify-center rounded border",
                            checked
                              ? "bg-primary border-primary text-primary-foreground"
                              : "border-border",
                          )}
                        >
                          {checked && <Check className="h-3 w-3" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Instance admin */}
            <div className="rounded-md border border-border p-3">
              <div className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-purple-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">Instance admin</p>
                  <p className="text-xs text-muted-foreground">
                    Full access to every practice, current and future — bypasses per-practice
                    membership. Grant is a no-op if they already have it; Revoke does nothing if
                    they don't.
                  </p>
                  {accessMember?.isInstanceAdmin && (
                    <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <Check className="h-3 w-3" /> Currently an instance admin
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => promoteMutation.mutate()}
                  disabled={promoteMutation.isPending || demoteMutation.isPending}
                >
                  {promoteMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  Grant instance admin
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => demoteMutation.mutate()}
                  disabled={promoteMutation.isPending || demoteMutation.isPending}
                >
                  {demoteMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  Revoke
                </Button>
              </div>
            </div>

            {accessError && <p className="text-sm text-destructive">{accessError}</p>}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAccessMember(null)}
              disabled={accessMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => accessMutation.mutate()}
              disabled={accessMutation.isPending || memberAccessLoading}
            >
              {accessMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              Save practices
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
